<template>
  <v-app>
    <v-app-bar color="primary" dark dense app>
      <v-app-bar-nav-icon
        v-if="loggedIn"
        @click.stop="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <v-toolbar-title>{{ siteTitle }} - {{ $route.path }} </v-toolbar-title>

      <v-spacer></v-spacer>
      <!-- Dark-mode toggle (restyle step 5). Always visible so the login page
           can be flipped too; defaults to the OS preference, this overrides it. -->
      <v-btn
        icon
        :title="$vuetify.theme.dark ? 'Switch to light mode' : 'Switch to dark mode'"
        aria-label="Toggle dark mode"
        @click="toggleDark"
      >
        <v-icon>{{ $vuetify.theme.dark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
      </v-btn>
      <v-btn
        v-if="loggedIn"
        color="primary"
        depressed
        light
        tile
        @click="doLogout"
      >
        Logout</v-btn
      >
    </v-app-bar>

    <v-navigation-drawer
      v-if="loggedIn"
      v-model="drawer"
      app
      temporary
    >
      <v-list dense nav>
        <v-list-item-group
          v-model="group"
          active-class="primary--text text--accent-4"
        >
          <!-- Tag capture mode -->
          <!-- Photo mode: Show Photo Tagging -->
          <v-list-item v-if="user.roles.includes('Tagger') && isPhotoMode && isTagMode" to="/tagging">
            <v-list-item-title>Photo Tagging</v-list-item-title>
          </v-list-item>

          <!-- Group mode: Show Group Tagging -->
          <v-list-item v-if="user.roles.includes('Tagger') && isGroupMode && isTagMode" to="/group-tagging">
            <v-list-item-title>Group Tagging</v-list-item-title>
          </v-list-item>

          <!-- Photo mode: Show Quality Control -->
          <v-list-item v-if="user.roles.includes('Quality Control') && isPhotoMode && isTagMode" to="/quality-control">
            <v-list-item-title>Quality Control</v-list-item-title>
          </v-list-item>

          <!-- Group mode: Show Group QC -->
          <v-list-item v-if="user.roles.includes('Quality Control') && isGroupMode && isTagMode" to="/group-qc">
            <v-list-item-title>Group Quality Control</v-list-item-title>
          </v-list-item>

          <!-- Count capture mode -->
          <!-- Photo mode: Show Photo Counting -->
          <v-list-item v-if="user.roles.includes('Tagger') && isPhotoMode && isCountMode" to="/counting">
            <v-list-item-title>Photo Counting</v-list-item-title>
          </v-list-item>

          <!-- Group mode: Show Group Counting -->
          <v-list-item v-if="user.roles.includes('Tagger') && isGroupMode && isCountMode" to="/group-counting">
            <v-list-item-title>Group Counting</v-list-item-title>
          </v-list-item>

          <!-- Photo mode: Show Count QC -->
          <v-list-item v-if="user.roles.includes('Quality Control') && isPhotoMode && isCountMode" to="/count-qc">
            <v-list-item-title>Count Quality Control</v-list-item-title>
          </v-list-item>

          <!-- Group mode: Show Group Count QC -->
          <v-list-item v-if="user.roles.includes('Quality Control') && isGroupMode && isCountMode" to="/group-count-qc">
            <v-list-item-title>Group Count QC</v-list-item-title>
          </v-list-item>

          <!-- Bounding-box capture mode -->
          <v-list-item v-if="user.roles.includes('Tagger') && isBboxMode" to="/box-tagging">
            <v-list-item-title>Box Tagging</v-list-item-title>
          </v-list-item>

          <!-- Bounding-box QC [IT-745] -->
          <v-list-item v-if="user.roles.includes('Quality Control') && isBboxMode" to="/box-qc">
            <v-list-item-title>Box Quality Control</v-list-item-title>
          </v-list-item>

          <!-- Tag management (tag mode) -->
          <v-list-item v-if="user.roles.includes('Sys Admin') && isTagMode" to="/manage-tags">
            <v-list-item-title>Manage Tags</v-list-item-title>
          </v-list-item>

          <!-- Count item management (count mode) -->
          <v-list-item v-if="user.roles.includes('Sys Admin') && isCountMode" to="/manage-count-items">
            <v-list-item-title>Manage Count Items</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="user.roles.includes('Auth Admin')" to="/manage-users">
            <v-list-item-title>Manage Users</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="user.roles.includes('Auth Admin')" to="/query-photos">
            <v-list-item-title>Query Photos</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="user.roles.includes('Quality Control') || user.roles.includes('Sys Admin')" to="/status">
            <v-list-item-title>Status</v-list-item-title>
          </v-list-item>

          <!-- Tag Verification (gated by feature flag + role) -->
          <v-list-item v-if="canSeeVerification" to="/verification">
            <v-list-item-title>Verification Runs</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="canSeeVerification" to="/verification/snapshots">
            <v-list-item-title>Verification Snapshots</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="canSeeVerification" to="/verification/rereview-batches">
            <v-list-item-title>Re-review Batches</v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>

    <v-alert
      v-if="message.alert"
      id="main-alert"
      v-model="message.alert"
      :type="message.type"
      class="d-flex align-self-center text-center"
      dark
      dismissible
      width="20%"
      style="position: fixed; top: 64px; left: 50%; transform: translateX(-50%); z-index: 9999; margin: 0"
    >
      {{ message.text }}
    </v-alert>
    <v-main>
      <transition name="page-fade" mode="out-in">
        <router-view></router-view>
      </transition>
    </v-main>
  </v-app>
</template>

<script>
// import HelloWorld from './components/HelloWorld.vue'
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  name: "App",
  components: {},
  data() {
    return {
      drawer: false,
      group: null,
      isInactive: false,
      userActivityThrottlerTimeout: null,
      userActivityTimeout: null,
      colorSchemeQuery: null,
    };
  },
  computed: {
    ...mapState({
      message: (state) => state.messages.message,
      loggedIn: (state) => state.auth.loggedIn,
      user: (state) => state.auth.profile.user,
      taggingMode: (state) => state.settings.taggingMode,
      captureMode: (state) => state.settings.captureMode,
      tagVerificationEnabled: (state) => state.settings.tagVerificationEnabled,
    }),
    canSeeVerification() {
      if (!this.tagVerificationEnabled) return false;
      const roles = (this.user && this.user.roles) || [];
      return roles.includes("Tag Verifier") || roles.includes("Sys Admin");
    },
    siteTitle() {
      return process.env.VUE_APP_SITE_TITLE || "Image Tagging";
    },
    isGroupMode() {
      return this.taggingMode === "group";
    },
    isPhotoMode() {
      return this.taggingMode === "photo";
    },
    isCountMode() {
      return this.captureMode === "count";
    },
    // Explicit equality, not "not count". With a third capture mode a
    // negative test silently makes bbox tenants look like tag tenants and
    // surfaces the wrong nav items.
    isTagMode() {
      return this.captureMode === "tag";
    },
    isBboxMode() {
      return this.captureMode === "bbox";
    },
  },
  created() {
    this.initTheme();
  },
  beforeMount() {
    let self = this;
    window.addEventListener("beforeunload", function (event) {
      self.clearAssignedPhotos();
      self.clearAssignedGroups();
      let confirmationMessage = "";
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    });
    this.activateActivityTracker();
  },
  mounted() {
    if (this.loggedIn) {
      // Settings first, to know tagging mode. The endpoint requires auth, so
      // logged-out boots skip it — Login.vue fetches it right after login,
      // and the router guard re-fetches on a hard refresh.
      this.fetchSettings();
      this.getCategoriesList();
      this.getTagsList();
      this.getPhotosList();
      this.getUsersList();
      this.getJobList();
      this.getProjectList();
      this.getRolesList();
      // this.getResponseIds();
      // this.getTaggerResponseIds();
    }
  },
  methods: {
    ...mapActions([
      "getCategoriesList",
      "getTagsList",
      "getPhotosList",
      "getUsersList",
      "getJobList",
      "getProjectList",
      "getRolesList",
      "getQCList",
      "getSAList",
      "clearAssignedPhotos",
      "clearAssignedGroups",
      "getResponseIds",
      "getTaggerResponseIds",
      "fetchSettings",
      "getCountItems",
    ]),
    ...mapMutations(["logout"]),
    doLogout() {
      this.clearAssignedPhotos();
      this.clearAssignedGroups();
      this.logout();
      this.$router.push("/login");
    },
    activateActivityTracker() {
      window.addEventListener("mousemove", this.userActivityThrottler);
    },
    resetUserActivityTimeout() {
      clearTimeout(this.userActivityTimeout);
      this.userActivityTimeout = setTimeout(() => {
        this.inactiveUserAction();
      }, process.env.VUE_APP_INACTIVE_TIME);
    },
    userActivityThrottler() {
      if (!this.userActivityThrottlerTimeout) {
        this.userActivityThrottlerTimeout = setTimeout(() => {
          this.resetUserActivityTimeout();

          clearTimeout(this.userActivityThrottlerTimeout);
          this.userActivityThrottlerTimeout = null;
        }, 1000);
      }
    },
    inactiveUserAction() {
      this.clearAssignedPhotos();
      this.clearAssignedGroups();
      this.doLogout();
    },
    initTheme() {
      // Dark mode (restyle step 5). Default to the OS preference; the app-bar
      // toggle (toggleDark) overrides it and is remembered in localStorage.
      // Taggers stare at photos for hours, so an explicit dark surface is a
      // real ergonomic win, not just chrome.
      let stored = null;
      try {
        stored = window.localStorage.getItem("tagging-theme");
      } catch (e) {
        stored = null; // private mode / storage disabled - fall back to auto
      }
      if (stored === "dark" || stored === "light") {
        this.$vuetify.theme.dark = stored === "dark";
      } else if (window.matchMedia) {
        this.$vuetify.theme.dark = window.matchMedia(
          "(prefers-color-scheme: dark)"
        ).matches;
      }
      // Follow later OS changes, but only while in auto mode (no stored
      // override) so a manual choice is never silently reverted.
      if (window.matchMedia) {
        this.colorSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
        // addEventListener is the modern API; addListener is the Safari <14
        // fallback (this legacy stack still runs on old WebKit).
        if (this.colorSchemeQuery.addEventListener) {
          this.colorSchemeQuery.addEventListener("change", this.onColorSchemeChange);
        } else if (this.colorSchemeQuery.addListener) {
          this.colorSchemeQuery.addListener(this.onColorSchemeChange);
        }
      }
    },
    onColorSchemeChange(event) {
      let override = null;
      try {
        override = window.localStorage.getItem("tagging-theme");
      } catch (e) {
        override = null;
      }
      if (override !== "dark" && override !== "light") {
        this.$vuetify.theme.dark = event.matches;
      }
    },
    toggleDark() {
      const next = !this.$vuetify.theme.dark;
      this.$vuetify.theme.dark = next;
      try {
        window.localStorage.setItem("tagging-theme", next ? "dark" : "light");
      } catch (e) {
        // storage unavailable - the toggle still applies for this session
      }
    },
  },
  beforeDestroy() {
    if (this.colorSchemeQuery) {
      if (this.colorSchemeQuery.removeEventListener) {
        this.colorSchemeQuery.removeEventListener("change", this.onColorSchemeChange);
      } else if (this.colorSchemeQuery.removeListener) {
        this.colorSchemeQuery.removeListener(this.onColorSchemeChange);
      }
    }
  },
};
</script>

<style>
/* FA Admin v2 typography (restyle step 2 - see docs/FRONTEND_RESTYLE_PLAN.md).
   One font; hierarchy from weight/size/letter-spacing. Left-aligned by default
   (this is a data-dense ops tool) - views that want centering set it locally. */
#app {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  letter-spacing: -0.005em;
  text-align: left;
  /* Body text color is set theme-scoped below (#app.theme--light/dark) so it
     flips in dark mode. Setting it here as a bare `#app` rule would win on ID
     specificity over any .theme--dark class rule and lock the app to the light
     slate. */
  margin-top: 0px;
}

/* Vuetify stamps font-family on .v-application; override it so buttons, chips,
   tables and dialogs pick up Plus Jakarta Sans too (app CSS loads after the
   vendor sheet, so this wins at equal specificity). */
.v-application {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

/* Nav drawer items: keep the brand-orange label. Now that the whole row is
   the router link (:to on v-list-item), Vuetify resets the title to the default
   dark list-text color, so restore the orange the items had before. The active
   item still stands out via its tonal background highlight. */
.v-navigation-drawer .v-list-item__title {
  color: var(--v-primary-base);
}

/* FA Admin v2 page-fade route transition (restyle step 4). 180ms opacity +
   small translateY on view change - the cheapest high-impact micro-interaction.
   out-in so the old view clears before the new one enters. */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}
.page-fade-enter,
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
@media (prefers-reduced-motion: reduce) {
  .page-fade-enter-active,
  .page-fade-leave-active {
    transition: none;
  }
  .page-fade-enter,
  .page-fade-leave-to {
    transform: none;
  }
}

/* A Vuetify temporary drawer + its scrim render at z-index 7, above the app
   bar (5), from top:0 - so when open the drawer covered the left of the header
   and the scrim dimmed it. Open the drawer below the 48px dense header, and
   lift the bar above the drawer/scrim so it stays bright. 8 clears both (7);
   dialogs (200+) remain above, and content menus (v-menu, 8) open below the
   bar so the tie is harmless. !important beats Vuetify's own app-bar z-index.
   No .v-overlay offset - that would wrongly shift dialog scrims too. */
header.v-app-bar {
  z-index: 8 !important;
}
.v-navigation-drawer--temporary {
  top: 48px !important;
  height: calc(100% - 48px) !important;
  max-height: calc(100% - 48px) !important;
}

/* FA Admin v2 surface rhythm (restyle step 4): contextual radius + soft,
   blue-tinted shadows on floating surfaces. Vuetify ships as precompiled CSS,
   so these are overrides. Radius is safe on every card (incl. outlined); the
   softened shadow is scoped to floating surfaces (dialogs, menus) + raised
   cards, so flat/outlined cards keep their intended borderless/bordered look. */
.v-application .v-card {
  border-radius: 12px;
}
.v-application .v-dialog > .v-card {
  border-radius: 14px;
}
.v-application .v-btn:not(.v-btn--round):not(.v-btn--icon):not(.v-btn--fab) {
  border-radius: 8px;
}
.v-application .v-dialog > .v-card,
.v-application .v-menu__content {
  box-shadow:
    0 16px 36px rgba(60, 80, 160, 0.14),
    0 2px 8px rgba(60, 80, 160, 0.06) !important;
}
.v-application .v-card--raised {
  box-shadow:
    0 6px 18px rgba(60, 80, 160, 0.1),
    0 1px 4px rgba(60, 80, 160, 0.05) !important;
}

/* FA Admin v2 tonal status chips (restyle step 4). Status chips across the
   QC / verification / count / photo views use solid semantic fills; render
   them tonal instead (dim-opacity tint + readable colored text + hairline
   border), per the dim-opacity convention. Filter chips carry no color, so
   they are untouched. Foregrounds are tuned for contrast - warning yellow
   gets a dark-olive text, primary orange a slightly darkened text. !important
   + the extra class beat Vuetify's solid color class and any forced white. */
.v-application .v-chip.success,
.v-application .v-chip.error,
.v-application .v-chip.warning,
.v-application .v-chip.info,
.v-application .v-chip.primary,
.v-application .v-chip.secondary {
  font-weight: 600;
  border: 1px solid transparent;
}
.v-application .v-chip.success   { background: rgba(1, 128, 100, 0.14) !important;  color: #017a5e !important; border-color: rgba(1, 128, 100, 0.3); }
.v-application .v-chip.error     { background: rgba(168, 19, 19, 0.12) !important;  color: #a81313 !important; border-color: rgba(168, 19, 19, 0.28); }
.v-application .v-chip.warning   { background: rgba(206, 205, 67, 0.22) !important; color: #6d6c0f !important; border-color: rgba(206, 205, 67, 0.45); }
.v-application .v-chip.info      { background: rgba(59, 109, 188, 0.15) !important; color: #335fa6 !important; border-color: rgba(59, 109, 188, 0.32); }
.v-application .v-chip.primary   { background: rgba(255, 106, 3, 0.13) !important;  color: #c9560a !important; border-color: rgba(255, 106, 3, 0.3); }
.v-application .v-chip.secondary { background: rgba(128, 161, 255, 0.2) !important; color: #4a66c7 !important; border-color: rgba(128, 161, 255, 0.45); }

/* ====================================================================
   FA Admin v2 dark mode (restyle step 5 - see docs/FRONTEND_RESTYLE_PLAN.md)

   Vuetify drives the base dark surfaces (body #121212, cards #1E1E1E, text)
   off $vuetify.theme.dark; everything below re-checks the step 2-4 overrides
   (which hardcode light-mode colors) so they stay legible on dark. All rules
   key off `.v-application.theme--dark` - the whole-app dark class on the
   v-app root - NOT a component's local `theme--dark` (the app bar carries the
   `dark` prop and so is always theme--dark regardless of the app theme).
   ==================================================================== */

/* Dark nav chrome (#0c0e13, plan 3.1): the app bar is color="primary" (orange)
   in light mode, but a bright orange bar is too heavy on a dark surface - tone
   both the bar and the drawer down to near-black. */
.v-application.theme--dark header.v-app-bar.primary {
  background-color: #0c0e13 !important;
}
.v-application.theme--dark .v-navigation-drawer {
  background-color: #0c0e13 !important;
}

/* Body text color, theme-scoped on #app itself. The mount node carries BOTH
   id="app" AND class="v-application theme--{light,dark}" (Vue mounts <v-app>
   onto #app), so a bare `#app { color }` wins on ID specificity over any
   `.theme--dark` class rule - which is why the dark text must also be keyed
   off #app (ID+class, 1,0,1) to beat it. Bare body text inherits this, so it
   flips in both themes. */
#app.theme--light {
  color: #2c3e50;
}
#app.theme--dark {
  color: rgba(255, 255, 255, 0.87);
}

/* Soft shadows go black (and heavier) on dark - the light, blue-tinted shadow
   family (rgba(60,80,160,.0x)) is invisible against a dark surface (plan 3.4). */
.v-application.theme--dark .v-dialog > .v-card,
.v-application.theme--dark .v-menu__content {
  box-shadow:
    0 16px 36px rgba(0, 0, 0, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.3) !important;
}
.v-application.theme--dark .v-card--raised {
  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.42),
    0 1px 4px rgba(0, 0, 0, 0.3) !important;
}

/* Tonal status chips: the dim-opacity tints keep working on dark, but the
   light-mode foregrounds (dark teal/olive/etc.) are unreadable on their own
   dark tint. Give each a light foreground and lift the fill/border alpha so
   the chip reads against #1E1E1E. */
.v-application.theme--dark .v-chip.success   { background: rgba(1, 128, 100, 0.22) !important;  color: #4ade9e !important; border-color: rgba(1, 128, 100, 0.5); }
.v-application.theme--dark .v-chip.error     { background: rgba(168, 19, 19, 0.26) !important;  color: #ff9a9a !important; border-color: rgba(168, 19, 19, 0.55); }
.v-application.theme--dark .v-chip.warning   { background: rgba(206, 205, 67, 0.22) !important; color: #e4e36a !important; border-color: rgba(206, 205, 67, 0.5); }
.v-application.theme--dark .v-chip.info      { background: rgba(59, 109, 188, 0.28) !important; color: #9ec0ff !important; border-color: rgba(59, 109, 188, 0.55); }
.v-application.theme--dark .v-chip.primary   { background: rgba(255, 106, 3, 0.22) !important;  color: #ffab6b !important; border-color: rgba(255, 106, 3, 0.5); }
.v-application.theme--dark .v-chip.secondary { background: rgba(128, 161, 255, 0.26) !important; color: #b9ccff !important; border-color: rgba(128, 161, 255, 0.55); }

/* Generic, repeated view classes (defined identically in many scoped view
   styles - .fixed footer bar, .photo-id/.photo-notes labels, .photo-image
   thumb border). A global dark rule beats the scoped .class[data-v] on
   specificity, so these are fixed once here instead of in every view. */
.v-application.theme--dark .fixed { background: #1e1e1e; }
.v-application.theme--dark .photo-notes { color: rgba(255, 255, 255, 0.9); }
.v-application.theme--dark .photo-id { color: rgba(255, 255, 255, 0.6); }
.v-application.theme--dark .photo-image { border-color: rgba(255, 255, 255, 0.18); }
</style>
